import { useEffect, useState } from "react";
import {
  DictionariesDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { Input, message, Modal, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { DictionariesService } from "../../services/dictionary.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { LanguageService } from "../../services/languages.service";
import { FaPlus } from "react-icons/fa";
const Dictionary = () => {
  const [dictionaries, setDictionaries] = useState<DictionariesDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] =
    useState<DictionariesDataType | null>(null);
  const [langCode, setLangCode] = useState("AZ");
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  useEffect(() => {
    setLoading(true);
    DictionariesService.getAllDictionaries()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: DictionariesDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setDictionaries(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await DictionariesService.getAllDictionaries();
    setDictionaries(
      updatedData?.data?.data?.map(
        (item: DictionariesDataType, index: number) => ({
          ...item,
          key: index + 1,
        })
      )
    );
  };

  const filteredDictionaries = dictionaries.filter(
    (item) =>
      item.word.toLowerCase().includes(searchText.toLowerCase()) ||
      item.meaning.toLowerCase().includes(searchText.toLowerCase())
  );
  const {
    handleSubmit,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
    handleChange,
  } = useFormik({
    initialValues: {
      word: "",
      meaning: "",
      translations: [] as { lang_code: string; text: string }[],
    },
    validationSchema: Yup.object({
      word: Yup.string()
        .required("Tərcümə olunacaq söz tələb olunur")
        .matches(
          /^(?!.*[\s-])(?=.*[a-zA-Z])[a-zA-Z0-9_]+$/,
          "Söz yalnız hərf və ya hərf, rəqəm və alt xətdən ibarət ola bilər, boşluq, tire və yanlızca rəqəm ola bilməz."
        ),
      meaning: Yup.string().required("Sözün tərcüməsi tələb olunur"),
    }),
    onSubmit: async (values) => {
      const azTranslation = {
        lang_code: "AZ",
        text: values.word,
      };
      const updatedTranslations = [
        azTranslation,
        ...values.translations.filter((t) => t.lang_code !== "AZ"),
      ];
      const finalValues = {
        ...values,
        translations: updatedTranslations,
      };

      if (isEditing && editingRecord) {
        if (isEditing && editingRecord) {
          const isEqual =
            editingRecord.word === values.word &&
            editingRecord.meaning === values.meaning &&
            editingRecord.translations === values.translations;
          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
          } else {
            const response = await DictionariesService.updateDictionary({
              ...finalValues,
              id: editingRecord.id,
            });

            if (response.status === 200) {
              message.success("Lüğət uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Lüğəti yeniləmək alınmadı."
              );
            }
          }
        }
      } else {
        const response = await DictionariesService.addDictionary(finalValues);
        if (response.status === 200) {
          message.success("Lüğət uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(response.errorMessage || "Lüğət əlavə etmək alınmadı.");
        }
      }
      setLoading(false);
    },
  });

  const handleTranslationChange = (value: string) => {
    if (langCode === "AZ") {
      setFieldValue("meaning", value);
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          text: value,
        };
      } else {
        updatedTranslations.push({ lang_code: langCode, text: value });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };
  // const handleDeleteClick = async (record: DictionariesDataType) => {
  //   try {
  //     await DictionariesService.deleteDictionary(record.id.toString());
  //     setDictionaries((prevData) =>
  //       prevData.filter((item) => item.id !== record.id)
  //     );
  //     message.success("Lüğət uğurla silindi!");
  //   } catch (error) {
  //     message.error("Lüğəti silmək alınmadı.");
  //   }
  // };

  const columns: TableProps<DictionariesDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Söz",
      dataIndex: "word",
      sorter: (a, b) => a.word.localeCompare(b.word),
    },
    {
      title: "Mənası",
      dataIndex: "meaning",
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          {/* <Popconfirm
            title="Bu lüğəti siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm> */}
        </div>
      ),
    },
  ];
  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
  };
  const handleEditClick = (record: DictionariesDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("word", record.word);
    setFieldValue("meaning", record.meaning);
    setFieldValue("translations", record.translations || []);
    setLangCode("AZ");
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Lüğətlər</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Lüğətlər</h3>
          </div>
          <div className="pages_components">
            <div className="funcional_head">
              <MainButton onClick={handleAddClick}>
                <FaPlus size={20} />
              </MainButton>
              <div className="search">
                <Input
                  style={{ borderColor: "#03a9f4" }}
                  placeholder="Söz axtarın..."
                  value={searchText}
                  onChange={(e) => {
                    e.persist();
                    setSearchText(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="pages_table">
              <Table<DictionariesDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={filteredDictionaries}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Lüğəti redaktə et" : "Yeni Lüğət əlavə et"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="word">
              Söz {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="word"
                id="word"
                placeholder="Söz"
                value={values.word}
                onChange={(e) => {
                  if (!isEditing) {
                    handleChange(e);
                  }
                }}
                disabled={isEditing}
                readOnly={isEditing}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.word} touched={touched.word} />
          </div>
          <div className="form_input">
            <label className="label" htmlFor="meaning">
              Mənası
            </label>
            <div className="input_wrapper">
              <input
                name="meaning"
                id="meaning"
                placeholder="Mənası"
                value={
                  langCode === "AZ"
                    ? values.meaning
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.text || ""
                }
                onChange={(e) => {
                  handleTranslationChange(e.target.value);
                }}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.meaning} touched={touched.meaning} />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Dictionary;
